/**
 * Structured postal addresses and the workshop's legal identity (owner, 2026-11-04). Pure, no imports: used by the
 * web app (through packages/core), the edge functions and the tests.
 *
 * Why parts: invoicing needs the postal code, the city and the street separately.
 * - Számlázz.hu Számla Agent (xmlszamla.xsd, checked 2026-11-04): vevo/irsz, vevo/telepules and vevo/cim are
 *   required strings, vevo/orszag is optional (free text); the seller's address comes from the Számlázz.hu account.
 * - NAV Online Számla 3.0 (invoiceBase.xsd SimpleAddressType): countryCode (ISO 3166 alpha-2), region (optional),
 *   postalCode (PostalCodeType, 3 to 10 letters, digits, spaces or hyphens; "0000" when the country has none),
 *   city and additionalAddressDetail (street, house number, floor, door), each at most 255 characters.
 * So an address here is: country (ISO alpha-2, default HU), postal code, city and one street line.
 *
 * Settings (settings.general) keep the old one string keys `seatAddress` and `workshopAddress` (older app versions,
 * the database functions and the edge functions read them); the parts sit beside them under
 * `<prefix>Country`, `<prefix>PostalCode`, `<prefix>City`, `<prefix>Street` with the prefixes "seat" and "workshop".
 * The app writes both on every save. The parts were filled from the old strings by
 * 20261222100000_legal_name_address.sql where the string could be split without doubt.
 */

export interface AddressParts {
  /** ISO 3166 alpha-2, upper case. */
  country: string;
  postalCode: string;
  city: string;
  /** Street, house number, floor and door as one line (NAV additionalAddressDetail, Számlázz.hu cim). */
  street: string;
}

export const DEFAULT_COUNTRY = "HU";

/**
 * The countries offered in the address fields: Hungary, its neighbours, the rest of the EU and EEA and the
 * countries our workshops' customers most often come from. Shown with their names in the user's language.
 */
export const ADDRESS_COUNTRIES: readonly string[] = [
  "HU", "AT", "SK", "UA", "RO", "RS", "HR", "SI",
  "BE", "BG", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "ES", "SE",
  "IS", "LI", "NO", "CH", "GB", "BA", "ME", "MK", "AL", "MD", "TR", "US",
];

export const emptyAddress = (country = DEFAULT_COUNTRY): AddressParts => ({ country, postalCode: "", city: "", street: "" });

const clean = (v: unknown) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");
const cleanCountry = (v: unknown) => {
  const c = clean(v).toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : DEFAULT_COUNTRY;
};

/** Postal code rules of the countries we know; others follow NAV's general rule. */
const POSTAL_RULES: Record<string, RegExp> = {
  HU: /^[1-9]\d{3}$/,
  AT: /^\d{4}$/,
  SI: /^\d{4}$/,
  CH: /^\d{4}$/,
  BE: /^\d{4}$/,
  DK: /^\d{4}$/,
  DE: /^\d{5}$/,
  HR: /^\d{5}$/,
  RS: /^\d{5}$/,
  UA: /^\d{5}$/,
  IT: /^\d{5}$/,
  FR: /^\d{5}$/,
  ES: /^\d{5}$/,
  RO: /^\d{6}$/,
  SK: /^\d{3} ?\d{2}$/,
  CZ: /^\d{3} ?\d{2}$/,
  PL: /^\d{2}-\d{3}$/,
};
/** NAV PostalCodeType: 3 to 10 characters, letters and digits, spaces or hyphens inside. */
const NAV_POSTAL = /^[A-Z0-9][A-Z0-9 -]{1,8}[A-Z0-9]$/i;

/** Whether the postal code fits the country (Hungary: 4 digits, not starting with 0). An empty code is not valid. */
export function isValidPostalCode(postalCode: string, country = DEFAULT_COUNTRY): boolean {
  const p = clean(postalCode);
  if (!p) return false;
  return (POSTAL_RULES[cleanCountry(country)] ?? NAV_POSTAL).test(p) && NAV_POSTAL.test(p);
}

export type AddressProblem = "postalCode" | "postalCodeFormat" | "city" | "street";

/**
 * What is wrong with an address. An empty address is fine unless `required`; once anything is given, the postal
 * code (in the country's format), the city and the street line are all needed (Számlázz.hu and NAV want all three).
 */
export function addressProblems(a: AddressParts, required = false): AddressProblem[] {
  const p = clean(a.postalCode);
  const c = clean(a.city);
  const s = clean(a.street);
  if (!p && !c && !s && !required) return [];
  const out: AddressProblem[] = [];
  if (!p) out.push("postalCode");
  else if (!isValidPostalCode(p, a.country)) out.push("postalCodeFormat");
  if (!c) out.push("city");
  if (!s) out.push("street");
  return out;
}

/** Whether nothing but the country is given. */
export const isEmptyAddress = (a: AddressParts | null | undefined) => !a || (!clean(a.postalCode) && !clean(a.city) && !clean(a.street));

/** The name of a country in a language ("Ausztria"), the code itself when the runtime does not know it. */
export function countryName(code: string, language = "hu"): string {
  try {
    return new Intl.DisplayNames([language], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * One line: "1151 Budapest, Mélyfúró u. 3." (the Hungarian order, also what the old single field held). The country
 * is added at the end when it is not `homeCountry` (by default Hungary), in the given language.
 */
export function formatAddress(a: AddressParts | null | undefined, opts: { language?: string; homeCountry?: string } = {}): string {
  if (!a) return "";
  const head = [clean(a.postalCode), clean(a.city)].filter(Boolean).join(" ");
  const line = [head, clean(a.street)].filter(Boolean).join(", ");
  const country = cleanCountry(a.country);
  if (!line) return "";
  return country === (opts.homeCountry ?? DEFAULT_COUNTRY) ? line : `${line}, ${countryName(country, opts.language)}`;
}

/**
 * A city name: no digits and none of , ; : / ( ) " # & + * @ ! ? < > = _ |, not starting with a space, dot, apostrophe
 * or hyphen ("Budapest XV. ker." and "Hódmezővásárhely" are fine). Same set as in public.split_hu_address.
 */
const CITY = /^[^\s.'’\-0-9][^0-9,;:/()"#&+*@!?<>=_|]*$/;

/**
 * Splits a Hungarian one line address into its parts, only when it is unambiguous: "1151 Budapest, Mélyfúró u. 3."
 * or "H-2111 Szada, Hajnal u. 32." (four digits, the city, a comma, the rest). A city without a street
 * ("1111 Budapest", one word) gives an empty street. Anything else gives null (the caller keeps the string as it is).
 * Same rule as public.split_hu_address in 20261222100000_legal_name_address.sql (a test checks it).
 */
export function parseHuAddress(raw: string | null | undefined): AddressParts | null {
  const a = clean(raw);
  if (!a) return null;
  const m = /^(?:(?:H|HU)\s*-\s*)?([1-9]\d{3})\.?\s+(.*)$/i.exec(a);
  if (!m) return null;
  // the city is up to the first comma, the rest is the street line
  const rest = m[2]!;
  const comma = rest.indexOf(",");
  const city = (comma < 0 ? rest : rest.slice(0, comma)).trim();
  const street = comma < 0 ? "" : rest.slice(comma + 1).trim();
  // without a comma only a one word city is sure ("1111 Budapest"); "1111 Budapest Fő utca" is not split
  if (!CITY.test(city) || (comma < 0 && /\s/.test(city))) return null;
  return { country: DEFAULT_COUNTRY, postalCode: m[1]!, city, street };
}

type General = Record<string, unknown>;
export type AddressPrefix = "seat" | "workshop";
const LEGACY_KEY: Record<AddressPrefix, string> = { seat: "seatAddress", workshop: "workshopAddress" };

/** The settings keys of an address's parts. */
export const addressKeys = (prefix: AddressPrefix) => ({
  country: `${prefix}Country`,
  postalCode: `${prefix}PostalCode`,
  city: `${prefix}City`,
  street: `${prefix}Street`,
});

/** The stored parts, without looking at the old string. */
function storedParts(g: General, prefix: AddressPrefix): AddressParts {
  const k = addressKeys(prefix);
  return { country: cleanCountry(g[k.country]), postalCode: clean(g[k.postalCode]), city: clean(g[k.city]), street: clean(g[k.street]) };
}

const norm = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

/**
 * The parts of the seat or the workshop address from the settings. When the old one line value is newer than the
 * parts (an older app version saved only that) or there are no parts yet, the line is split; if it cannot be split
 * without doubt, the whole line goes into the street field and `unsplit` is true, so nothing is lost and the form
 * can ask the user to check it. The seat falls back to the old `workshopAddress` (before 2026-11-04 the only address).
 */
export function addressPartsOf(general: General, prefix: AddressPrefix): AddressParts & { unsplit: boolean } {
  const parts = storedParts(general, prefix);
  const legacy = clean(general[LEGACY_KEY[prefix]]) || (prefix === "seat" ? clean(general.workshopAddress) : "");
  const fromParts = formatAddress(parts, { homeCountry: parts.country });
  if (!legacy || (!isEmptyAddress(parts) && norm(legacy).startsWith(norm(fromParts)))) return { ...parts, unsplit: false };
  const parsed = parseHuAddress(legacy);
  if (parsed) return { ...parsed, country: isEmptyAddress(parts) ? DEFAULT_COUNTRY : parts.country, unsplit: false };
  return { ...emptyAddress(parts.country), street: legacy, unsplit: true };
}

/** Whether the workshop address is the seat (the tick, default on). */
const sameAsSeat = (g: General) => !(g.workshopAddressSameAsSeat === false || g.workshopAddressSameAsSeat === "false");

/** The parts of where the car is: the seat while the tick is on, otherwise the workshop address (the seat if empty). */
export function siteAddressPartsOf(general: General): AddressParts & { unsplit: boolean } {
  if (sameAsSeat(general)) return addressPartsOf(general, "seat");
  const site = addressPartsOf(general, "workshop");
  return isEmptyAddress(site) ? addressPartsOf(general, "seat") : site;
}

/**
 * The settings values to save for an address: the parts and the old one line key computed from them (older readers).
 * `language` names a foreign country in the line.
 */
export function addressSettings(prefix: AddressPrefix, a: AddressParts, language = "hu"): Record<string, string | null> {
  const k = addressKeys(prefix);
  const line = formatAddress(a, { language });
  return {
    [k.country]: cleanCountry(a.country),
    [k.postalCode]: clean(a.postalCode) || null,
    [k.city]: clean(a.city) || null,
    [k.street]: clean(a.street) || null,
    [LEGACY_KEY[prefix]]: line || null,
  };
}

// ---- the legal identity ------------------------------------------------------------------------------------

/**
 * Who runs the workshop (owner, 2026-11-04): a workshop is often known by a trade name ("Sanyi szervize", the
 * workshop's name shown big everywhere) while the business is a company or a sole trader ("Vitamin Kft.",
 * "Kiss Sándor e.v."). The seat, the tax number and the registration number belong to that business.
 */
export interface LegalIdentity {
  /** The official name (settings `legalName`; filled from the contract), null when not given. */
  name: string | null;
  /** The registered seat as one line. */
  seat: string | null;
  taxNumber: string | null;
  /** Cégjegyzékszám of a company or nyilvántartási szám of a sole trader. */
  regNumber: string | null;
}

export function legalIdentityOf(general: unknown): LegalIdentity {
  const g = (general && typeof general === "object" ? general : {}) as General;
  const seat = addressPartsOf(g, "seat");
  return {
    name: clean(g.legalName) || null,
    seat: formatAddress(seat) || null,
    taxNumber: clean(g.workshopTaxNumber) || null,
    regNumber: clean(g.workshopRegNumber) || null,
  };
}

/** Whether two names are the same apart from letter case and spaces ("Kovács Autó Kft." = "kovács  autó kft."). */
export const sameName = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && clean(a).toLowerCase().replace(/\s/g, "") === clean(b).toLowerCase().replace(/\s/g, "");

/** A company registration number (cégjegyzékszám, 01-09-123456); anything else counts as a sole trader's number. */
export const isCompanyRegNumber = (reg: string | null | undefined) => /^\d{2}\s*-\s*\d{2}\s*-\s*\d{6}$/.test(clean(reg));

export interface OperatorLabels {
  /** "Üzemeltető" */
  operator: string;
  /** "Adószám" */
  taxNumber: string;
  /** "Cégjegyzékszám" */
  regNumber: string;
  /** "Nyilvántartási szám" (a sole trader) */
  soleTraderNumber: string;
}

/**
 * The small legal line under the trade name on the printed documents, the customer's pages and the e-mails:
 * "Üzemeltető: Vitamin Kft. · 1151 Budapest, Mélyfúró u. 3. · Adószám: 12345678-2-42 · Cégjegyzékszám: 01-09-123456".
 * Missing parts are left out; the name is left out when it is the trade name itself (shown only once). Null when
 * there is nothing to show.
 */
export function operatorLine(legal: LegalIdentity | null | undefined, tradeName: string | null | undefined, labels: OperatorLabels): string | null {
  if (!legal) return null;
  const parts = [
    legal.name && !sameName(legal.name, tradeName) ? `${labels.operator}: ${legal.name}` : "",
    legal.seat ?? "",
    legal.taxNumber ? `${labels.taxNumber}: ${legal.taxNumber}` : "",
    legal.regNumber ? `${isCompanyRegNumber(legal.regNumber) ? labels.regNumber : labels.soleTraderNumber}: ${legal.regNumber}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}
